import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { formatApiErrorDetail } from "../lib/api";
import { startStripeCheckout } from "../lib/payments";
import { toast } from "sonner";
import { Check } from "lucide-react";

const TIERS = [
  {
    id: "silver",
    package_id: "sub_silver_monthly",
    name: "Silver",
    price: "$49",
    cadence: "per month",
    tagline: "For the curious.",
    features: [
      "Unlimited editorial archive",
      "Silver-tier interviews",
      "Monthly Dispatch newsletter",
      "Directory access (read-only)",
    ],
  },
  {
    id: "gold",
    package_id: "sub_gold_monthly",
    name: "Gold",
    price: "$149",
    cadence: "per month",
    tagline: "For the engaged.",
    featured: true,
    features: [
      "Everything in Silver",
      "Gold-tier long-form interviews",
      "Reportage and profile archives",
      "Quarterly print companion",
      "Priority invitations to in-person salons",
    ],
  },
  {
    id: "platinum",
    package_id: "sub_platinum_monthly",
    name: "Platinum",
    price: "$499",
    cadence: "per month",
    tagline: "For the principal.",
    features: [
      "Everything in Gold",
      "Platinum tier (reserved interviews)",
      "Annotated transcripts & footnotes",
      "Private member directory inclusion",
      "Direct line to the editorial desk",
      "Annual private dinner (by invitation)",
    ],
  },
];

export default function Subscribe() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [pendingId, setPendingId] = useState(null);

  const select = async (tier) => {
    if (!user) {
      toast.info("Please create an account to subscribe.");
      navigate(`/register?next=/subscribe`);
      return;
    }
    if (user?.subscription_active && user?.subscription_tier === tier.id) {
      toast.info("You are already on this plan.");
      return;
    }
    setPendingId(tier.id);
    try {
      await startStripeCheckout({
        package_id: tier.package_id,
        rail: "stripe",
        metadata: {
          customer_id: user.id,
          email: user.email,
          tier: tier.id,
          subscription_purchase: "true",
        },
        success_path: "/subscribe/success",
        cancel_path: "/subscribe",
      });
      // startStripeCheckout redirects the browser to Stripe on success.
    } catch (e) {
      setPendingId(null);
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not start checkout.");
    }
  };

  return (
    <div data-testid="subscribe-page" className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-32 pb-24">
      <div className="text-center mb-20 reveal">
        <p className="eyebrow mb-4">Membership</p>
        <h1 className="font-display text-6xl md:text-7xl mb-6 max-w-3xl mx-auto leading-[0.95]">
          Three quiet tiers. <em style={{ color: "var(--brass)" }}>One archive.</em>
        </h1>
        <p className="text-lg max-w-2xl mx-auto leading-relaxed" style={{ color: "var(--ink-muted)" }}>
          Wealth First is supported entirely by its members. No advertising. No sponsorships. No exit. Cancel anytime.
        </p>
        {user?.subscription_active && (
          <p className="mt-6 text-sm" style={{ color: "var(--brass)" }}>
            You are currently subscribed to <strong>{user.subscription_tier?.toUpperCase()}</strong>.
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {TIERS.map((t) => {
          const isCurrent = user?.subscription_active && user?.subscription_tier === t.id;
          const isPending = pendingId === t.id;
          return (
            <div
              key={t.id}
              data-testid={`tier-card-${t.id}`}
              className="relative p-10 flex flex-col"
              style={{
                border: t.featured ? "1px solid var(--brass)" : "1px solid var(--hairline)",
                background: t.featured ? "var(--surface)" : "transparent",
              }}
            >
              {t.featured && (
                <div className="absolute -top-3 left-10 px-3 py-1 text-[0.625rem] uppercase tracking-[0.22em]" style={{ background: "var(--bg)", color: "var(--brass)", border: "1px solid var(--brass)" }}>
                  Most Selected
                </div>
              )}
              <p className="eyebrow mb-4">{t.tagline}</p>
              <h3 className="font-display text-5xl mb-2">{t.name}</h3>
              <div className="flex items-baseline gap-2 mb-8">
                <span className="font-display text-4xl" style={{ color: "var(--brass)" }}>{t.price}</span>
                <span className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--ink-faint)" }}>{t.cadence}</span>
              </div>
              <hr className="hairline mb-8" />
              <ul className="space-y-4 mb-10 flex-1">
                {t.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm leading-relaxed">
                    <Check size={14} className="mt-1 shrink-0" style={{ color: "var(--brass)" }} />
                    <span style={{ color: "var(--ink)" }}>{f}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => select(t)}
                data-testid={`subscribe-${t.id}-btn`}
                disabled={isCurrent || isPending}
                className={t.featured ? "btn-brass justify-center w-full" : "btn-ghost justify-center w-full"}
                style={{ justifyContent: "center" }}
              >
                {isCurrent ? "Current Plan" : isPending ? "Redirecting to Stripe…" : `Choose ${t.name}`}
              </button>
            </div>
          );
        })}
      </div>

      <p className="text-center mt-12 text-xs uppercase tracking-[0.22em]" style={{ color: "var(--ink-faint)" }}>
        Secure checkout by Stripe · Card, ACH, Apple Pay, Google Pay · Cancel anytime
      </p>
    </div>
  );
}
